import { useEffect, useMemo, useState } from 'react'
import { Users, Pencil, Check, X, RotateCcw, AlertCircle, Award, Trash2 } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/ui/card'
import { Modal } from '@/ui/modal'
import { useClassSessionStore } from '@/features/classes/stores'
import type { SessionAttendee, Student } from '@/features/classes/stores'
import { cn } from '@/core/lib/utils'
import { useIsMounted } from '@/core/hooks/useIsMounted'

import { AttendeeStatusBadge } from './StatusBadges'
import { StudentHistoryModal } from './StudentHistoryModal'

interface InlineInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  className?: string
}

function InlineInput({ className, ...props }: InlineInputProps) {
  return (
    <input
      className={cn(
        'border border-slate-150 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary',
        className
      )}
      {...props}
    />
  )
}

interface EditState {
  attendeeId: string
  firstName: string
  lastName: string
  phone: string
  company: string
}

interface DeleteTarget {
  id: string
  name: string
  email: string
  status: SessionAttendee['status']
}

interface SessionAttendeesTabProps {
  sessionId: string
  passingScore: number
}

export function SessionAttendeesTab({ sessionId, passingScore }: SessionAttendeesTabProps) {
  const mounted = useIsMounted()
  const sessions = useClassSessionStore((s) => s.sessions)
  const loadSessionAttendees = useClassSessionStore((s) => s.loadSessionAttendees)
  const updateAttendeeContact = useClassSessionStore((s) => s.updateAttendeeContact)
  const markAttendeeCompleted = useClassSessionStore((s) => s.markAttendeeCompleted)
  const deleteAttendee = useClassSessionStore((s) => s.deleteAttendee)
  const rawAttendees = useClassSessionStore((s) => s.attendees)
  const rawStudents = useClassSessionStore((s) => s.students)

  const session = sessions.find((s) => s.id === sessionId)

  const attendees = useMemo((): Array<SessionAttendee & { student: Student }> =>
    rawAttendees
      .filter((a) => a.sessionId === sessionId)
      .map((a) => ({ ...a, student: rawStudents.find((st) => st.id === a.studentId) }))
      .filter((a): a is SessionAttendee & { student: Student } => Boolean(a.student)),
  [rawAttendees, rawStudents, sessionId])

  const [editState, setEditState] = useState<EditState | null>(null)
  const [saveLoading, setSaveLoading] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [attendeesLoading, setAttendeesLoading] = useState(true)
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set())
  const [historyAttendeeId, setHistoryAttendeeId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  useEffect(() => {
    if (!session) {return}

    setAttendeesLoading(true)
    void (async () => {
      try {
        await loadSessionAttendees(sessionId)
      } finally {
        if (mounted.current) {
          setAttendeesLoading(false)
        }
      }
    })()
  }, [session, sessionId, loadSessionAttendees, mounted])

  function startEdit(a: { id: string; student: { firstName: string; lastName: string; phone: string; company: string } }) {
    setSaveError(null)
    setEditState({
      attendeeId: a.id,
      firstName: a.student.firstName,
      lastName: a.student.lastName,
      phone: a.student.phone,
      company: a.student.company,
    })
  }

  function cancelEdit() {
    setEditState(null)
    setSaveError(null)
  }

  async function saveEdit() {
    if (!editState) {return}
    setSaveLoading(true)
    setSaveError(null)
    try {
      await updateAttendeeContact(editState.attendeeId, {
        firstName: editState.firstName,
        lastName: editState.lastName,
        phone: editState.phone,
        company: editState.company,
      })
      setEditState(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error saving')
    } finally {
      setSaveLoading(false)
    }
  }

  async function markCompleted(attendeeId: string) {
    setCompletingIds((prev) => new Set(prev).add(attendeeId))
    setSaveError(null)
    try {
      await markAttendeeCompleted(attendeeId)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error marking completed')
    } finally {
      setCompletingIds((prev) => {
        const next = new Set(prev)
        next.delete(attendeeId)
        return next
      })
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) {return}
    setDeleteLoading(true)
    setSaveError(null)
    try {
      await deleteAttendee(deleteTarget.id)
      setDeleteTarget(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Error deleting student')
    } finally {
      setDeleteLoading(false)
    }
  }

  const withScore = attendees.filter((a) => a.quizScore !== null)
  const avg = withScore.length > 0
    ? Math.round(withScore.reduce((s, a) => s + (a.quizScore ?? 0), 0) / withScore.length)
    : null

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-navy flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          Registered Students ({attendees.length})
        </CardTitle>
        <CardDescription>
          Click the pencil to correct a student&apos;s data
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        {saveError && (
          <div className="mx-4 mb-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {saveError}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead>
              <tr className="border-b border-slate-150 bg-gray-50">
                <th className="text-left px-4 py-3 font-medium text-slate-500">Name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500 hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500">Phone</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500 hidden md:table-cell">Company</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500">Score</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500">Check-in</th>
                <th className="text-left px-4 py-3 font-medium text-slate-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {attendeesLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center">
                    <RotateCcw className="w-5 h-5 text-slate-150 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : attendees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-sm">
                    No paid or QR-registered students yet
                  </td>
                </tr>
              ) : (
                attendees.map((a) => {
                  const isEditing = editState?.attendeeId === a.id
                  return (
                    <tr
                      key={a.id}
                      className={cn(
                        'border-b border-slate-150 transition-colors',
                        isEditing ? 'bg-amber-50' : 'hover:bg-gray-50'
                      )}
                    >
                      <td className="px-4 py-3 font-medium text-navy">
                        {isEditing ? (
                          <div className="flex gap-1">
                            <InlineInput
                              className="w-24"
                              value={editState.firstName}
                              onChange={(e) => setEditState({ ...editState, firstName: e.target.value })}
                              placeholder="First Name"
                            />
                            <InlineInput
                              className="w-24"
                              value={editState.lastName}
                              onChange={(e) => setEditState({ ...editState, lastName: e.target.value })}
                              placeholder="Last Name"
                            />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setHistoryAttendeeId(a.id)}
                            className="hover:text-primary hover:underline text-left"
                            title="View student history"
                          >
                            {`${a.student.firstName} ${a.student.lastName}`}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">
                        {a.student.email || '—'}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {isEditing ? (
                          <InlineInput
                            className="w-32"
                            value={editState.phone}
                            onChange={(e) => setEditState({ ...editState, phone: e.target.value })}
                            placeholder="Phone"
                          />
                        ) : (
                          a.student.phone || '—'
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-500 hidden md:table-cell">
                        {isEditing ? (
                          <InlineInput
                            className="w-32"
                            value={editState.company}
                            onChange={(e) => setEditState({ ...editState, company: e.target.value })}
                            placeholder="Company"
                          />
                        ) : (
                          a.student.company || '—'
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.quizScore !== null ? (
                          <span className={cn('font-semibold', a.quizScore >= passingScore ? 'text-green-600' : 'text-red-500')}>
                            {a.quizScore}%
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {a.checkedInAt ? (
                          <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                            <Check className="w-3.5 h-3.5" />
                            {new Date(a.checkedInAt).toLocaleDateString('en-CA')}
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <AttendeeStatusBadge status={a.status} />
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={saveEdit}
                              disabled={saveLoading}
                              className="p-1.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                              title="Save"
                              aria-label="Save student changes"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={saveLoading}
                              className="p-1.5 rounded hover:bg-red-100 text-red-500 disabled:opacity-50"
                              title="Cancel"
                              aria-label="Cancel editing"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => startEdit(a)}
                              className="p-1.5 rounded hover:bg-slate-150 text-slate-500"
                              title="Edit student data"
                              aria-label="Edit student data"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() =>
                                setDeleteTarget({
                                  id: a.id,
                                  name: `${a.student.firstName} ${a.student.lastName}`.trim(),
                                  email: a.student.email,
                                  status: a.status,
                                })
                              }
                              className="p-1.5 rounded hover:bg-red-100 text-red-500"
                              title="Delete student"
                              aria-label="Delete student"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                            {a.status !== 'completed' && (
                              <button
                                onClick={() => markCompleted(a.id)}
                                disabled={completingIds.has(a.id)}
                                className="p-1.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                                title="Mark as completed"
                                aria-label="Mark as completed"
                              >
                                {completingIds.has(a.id) ? (
                                  <RotateCcw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Award className="w-4 h-4" />
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Summary row */}
        {attendees.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-150 text-xs text-slate-500 flex gap-6">
            <span>{attendees.length} student{attendees.length !== 1 ? 's' : ''} registered</span>
            <span>{withScore.length} diagnostic{withScore.length !== 1 ? 's' : ''} completed</span>
            {avg !== null && <span>Average: <strong className="text-navy">{avg}%</strong></span>}
          </div>
        )}
      </CardContent>
      <StudentHistoryModal
        attendeeId={historyAttendeeId}
        open={historyAttendeeId !== null}
        onClose={() => setHistoryAttendeeId(null)}
      />
      <Modal open={deleteTarget !== null} onClose={() => {if (!deleteLoading) {setDeleteTarget(null)}}}>
        <h3 className="text-base font-semibold text-navy">Delete student</h3>
        <p className="mt-2 text-sm text-slate-600">
          Remove <strong>{deleteTarget?.name}</strong> ({deleteTarget?.email}) from this session?
        </p>
        {deleteTarget?.status === 'completed' && (
          <p className="mt-2 text-sm text-amber-600">
            This student may have a certificate — deletion will be blocked.
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setDeleteTarget(null)}
            disabled={deleteLoading}
            className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDelete}
            disabled={deleteLoading}
            className="px-3 py-1.5 rounded bg-red-600 text-sm text-white hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {deleteLoading && <RotateCcw className="w-3.5 h-3.5 animate-spin" />}
            Delete
          </button>
        </div>
      </Modal>
    </Card>
  )
}
